<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { useValidation } from "@/composables/useValidation";
import type { DispatcherDto } from "@/api/dispatchers/dto/dispatcher.dto";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import { useBaseView } from "@/composables";
import PhoneField from "@/components/inputs/PhoneField.vue";
import { phoneExtMask } from "@/constants/mask";
import EmailField from "@/components/inputs/EmailField.vue";
import { Role } from "@/constants/navigation";
import type { DispatcherGroups } from "@/api/dispatchers/dto/dispatcher-groups";
import { RoleGroupMap } from "@/api/dispatchers/dto/dispatcher-groups";
import type { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { computed, ref, watch } from "vue";
import { useAppStore } from "@/store/app.store";

const { t } = useI18n();
const { requiredValidator, selectValidator, phoneExtValidator } = useValidation();
const { roles } = useBaseView();
const appStore = useAppStore();

const model = defineModel<DispatcherDto>({
  default: {
    id: null,
    name: "",
    firstName: "",
    lastName: "",
    roles: [],
    phone: "",
    phoneExt: "",
    timezone: "",
    company: {
      id: 0,
      name: "",
      code: "",
    },
    isSuperAdmin: false,
    permissions: [],
  },
});

const props = defineProps<{
  groups: DispatcherGroups | null;
}>();

const previousRoles = ref<Role[]>([...(model.value.roles || [])]);

const allPermissions = computed<DispatcherPermissionValue[]>(() => {
  if (!appStore.dispatcher) {
    return [];
  }

  return appStore.dispatcher.allExistingPermissions.flatMap((group) =>
    group.permissions.map((permission) => permission.value),
  );
});

function getPermissionsByRole(role: Role): DispatcherPermissionValue[] {
  const group = RoleGroupMap[role];

  if (!group || !props.groups) {
    return [];
  }

  return (props.groups[group] ?? []).map((permission) => permission.key);
}

function updatePermissionByRoles(newRoles: Role[]) {
  const hasAdmin = newRoles.some((role) => role === Role.admin);

  if (hasAdmin) {
    model.value.permissions = allPermissions.value;
    previousRoles.value = [...newRoles];
    return;
  }

  const grantedByNewRoles = newRoles.flatMap(getPermissionsByRole);
  const addedRoles = newRoles.filter((role) => !previousRoles.value.includes(role));
  const removedRoles = previousRoles.value.filter((role) => !newRoles.includes(role));

  const permissionsToAdd = addedRoles.flatMap(getPermissionsByRole);
  const permissionsToRemove = removedRoles
    .flatMap(getPermissionsByRole)
    .filter((permission) => !grantedByNewRoles.includes(permission));

  const keptPermissions = model.value.permissions.filter((permission) => !permissionsToRemove.includes(permission));
  const nextPermissions = [...keptPermissions, ...permissionsToAdd];

  model.value.permissions = [...new Set(nextPermissions)];
  previousRoles.value = [...newRoles];
}

watch(
  () => model.value.roles,
  (roles) => {
    if (roles.length) {
      updatePermissionByRoles(model.value.roles);
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <v-card class="pa-5">
    <div class="font-size-11 text-grey-darken-1">
      {{ t("general.dispatcher-information") }}
    </div>

    <v-row class="mt-3">
      <v-col>
        <v-text-field
          v-model="model.firstName"
          :label="t('general.first-name')"
          :rules="[requiredValidator]"
          autocomplete="off"
          class="required"
          color="primary"
          density="compact"
          variant="outlined"
        />
      </v-col>

      <v-col>
        <v-text-field
          v-model="model.lastName"
          :label="t('general.last-name')"
          :rules="[requiredValidator]"
          autocomplete="off"
          class="required"
          color="primary"
          density="compact"
          variant="outlined"
        />
      </v-col>

      <v-col>
        <email-field v-model="model.email" :rules="[requiredValidator]" />
      </v-col>

      <v-col>
        <omni-select
          v-model="model.roles"
          :items="roles"
          :label="t('dispatchers.roles')"
          :multiple="true"
          :rules="[selectValidator]"
          item-title="name"
          item-value="id"
          required
          @update:model-value="updatePermissionByRoles"
        />
      </v-col>

      <v-col>
        <phone-field v-model="model.phone" :label="t('general.phone')" required />
      </v-col>

      <v-col>
        <v-text-field
          v-model="model.phoneExt"
          v-maska="phoneExtMask"
          :label="t('dispatchers.phone-ext')"
          :rules="[phoneExtValidator]"
          autocomplete="off"
          color="primary"
          density="compact"
          variant="outlined"
      /></v-col>
    </v-row>
  </v-card>
</template>

<style lang="scss" scoped></style>
